import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import BoxIcon from '@sdk/shifts/components/BoxIcon';
import EventBoxContainerStyle, {
  EventBoxContainerProps,
} from '@sdk/schedule/components/EventBoxContainerStyle';
import type { NoShowType } from '@sdk/noShows/types/NoShowType';
import EventBoxOuter from '@sdk/schedule/components/EventBoxOuter';
import EventBoxInner from '@sdk/schedule/components/EventBoxInner';
import {
  EventBoxCommonBaseProps,
  EventBoxSelectableProps,
} from '@sdk/schedule/types/EventBoxProps';

import NoShowIcon from '../assets/svg/Icons/24/no-show-white.svg';

import type { OpenEventCallback } from './types';
import FlexRow from './ui/FlexRow';
import EventBoxSelect from './components/EventBoxSelect';
import { EVENT_BOX_HEIGHT } from './windowed/constants';
import getAbsenceColors from './helpers/getAbsenceColors';
import getRenderBoxLine from './helpers/getRenderBoxLine';

type BoxProps = {
  bgHover: string;
  borderColorHover: string;
} & EventBoxContainerProps;

const Box = styled(FlexRow)<BoxProps>`
  ${EventBoxContainerStyle}

  background-color: ${({ bg }): string => bg};
  border-color: ${({ borderColor }): string => borderColor};

  ${EventBoxOuter}:hover & {
    background-color: ${({ bgHover }): string => bgHover};
    border-color: ${({ borderColorHover }): string => borderColorHover};
  }
`;

type Props = {
  noShow: NoShowType;
  openEvent: OpenEventCallback;
  isHovered: boolean;
} & EventBoxCommonBaseProps &
  EventBoxSelectableProps;

const NoShowBox: React.FC<React.PropsWithChildren<Props>> = ({
  hoverComponent,
  noShow,
  openEvent,
  shiftAppearanceMode,
  isSelected,
  showSelect,
  toggleSelection,
  height,
  showPlus,
  isHovered,
}) => {
  const { colors } = useTheme();
  const shiftAppearance = noShow.shiftAppearance?.[shiftAppearanceMode];
  const { bg, bgHover, borderColor, borderColorHover, textColor, textColorHover } =
    getAbsenceColors(noShow, colors);
  const { firstLine, secondLine, thirdLine, fourthLine } = shiftAppearance || {};

  const onClick = (evt: React.MouseEvent<HTMLDivElement>): void => {
    openEvent({ event: noShow, isExchange: false, $event: evt });
  };

  const renderBoxLine = getRenderBoxLine({
    color: textColor,
    hoverColor: textColorHover,
    hoverComponent: EventBoxOuter,
  });

  return (
    <EventBoxOuter>
      <Box
        hoverComponent={hoverComponent}
        bg={bg}
        bgHover={bgHover}
        borderColor={borderColor}
        borderColorHover={borderColorHover}
        showPlus={showPlus}
        onClick={onClick}
        height={height || EVENT_BOX_HEIGHT}>
        {(isHovered || isSelected || showSelect) && noShow.canBeSelected && !noShow.foreign && (
          <EventBoxSelect
            isSelected={isSelected}
            toggleSelection={toggleSelection}
            showSelect={showSelect}
            hoverComponent={EventBoxOuter}
          />
        )}
        <EventBoxInner>
          {renderBoxLine(firstLine, true)}
          {secondLine && renderBoxLine(secondLine)}
          {thirdLine && renderBoxLine(thirdLine)}
          {fourthLine && renderBoxLine(fourthLine)}
        </EventBoxInner>
        <BoxIcon source={NoShowIcon} color={colors.white} />
      </Box>
    </EventBoxOuter>
  );
};

export default NoShowBox;
